/**
 * The pre-recorded greeting, in the agent editor: have the agent say its
 * greeting once in its own voice, keep that as the clip, hear it, delete it.
 *
 * WHY NOT A MICROPHONE OR A FILE. A clip recorded by a person can never sound
 * like the agent, and the caller would hear one voice greet them and another
 * answer. So the only way a clip is made is a greeting-render call: a short
 * call from this page in which the agent speaks its SAVED greeting, the server
 * records exactly what the model produced, checks it said the greeting word for
 * word, and stores it (session-do's greeting-render.ts). The person hears the
 * agent speak it as it happens, and only needs to stay quiet.
 *
 * THE SAVED AGENT, NOT THE FORM. The render call reads the agent as stored, so
 * it is blocked while the greeting, voice or model have unsaved edits — a clip
 * of the old words or the old voice is exactly what this must not produce.
 */
import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { PlayIcon, SparklesIcon, SquareIcon, Trash2Icon } from 'lucide-react'
import { ApiError, agents, greetingAudioPath, type GreetingAudioInfo } from '@/api'
import { CallSession } from '@/lib/call'
import { Button } from '@/components/ui/button'
import { fmtWhen } from '@/components/state-pill'

/** One clip audible at a time. */
let current: { stop(): void } | null = null
function stopCurrent() {
  current?.stop()
  current = null
}

async function playWav(bytes: ArrayBuffer, onDone: () => void): Promise<void> {
  stopCurrent()
  const ctx = new AudioContext()
  try {
    const buffer = await ctx.decodeAudioData(bytes)
    const src = ctx.createBufferSource()
    src.buffer = buffer
    src.connect(ctx.destination)
    const handle = {
      stop() {
        try {
          src.stop()
        } catch {
          /* already stopped */
        }
      },
    }
    src.onended = () => {
      if (current === handle) current = null
      void ctx.close().catch(() => {})
      onDone()
    }
    current = handle
    src.start()
  } catch {
    void ctx.close().catch(() => {})
    onDone()
  }
}

const secs = (ms: number) => `${(ms / 1000).toFixed(1)} s`

/** Why a render failed, in words a person can act on. */
function explain(reason: string | undefined, heard: string | undefined): string {
  switch (reason) {
    case 'no_speech':
      return 'The agent did not say anything. Try again.'
    case 'did_not_match':
      return heard
        ? `The agent said “${heard}”, which is not exactly the greeting. Nothing was saved; try again.`
        : 'The agent did not say the greeting exactly. Nothing was saved; try again.'
    case 'caller_spoke':
      return 'Someone spoke while the agent was greeting. Stay quiet until it finishes, and try again.'
    case 'too_long':
      return 'The greeting took longer than 20 s to say. Shorten it and try again.'
    case 'save_failed':
      return 'The clip could not be saved. Try again.'
    default:
      return 'The greeting could not be captured. Try again.'
  }
}

/** Why a saved clip no longer plays, or null when it is current. */
function staleness(info: GreetingAudioInfo, voice: string, greeting: string | null): string | null {
  if (info.voice === null) return 'it was not made by the agent'
  if (info.voice !== voice) return `it is in the ${info.voice} voice, and the agent now speaks as ${voice}`
  if (info.transcript.trim() !== (greeting ?? '').trim()) return `it says “${info.transcript}”, and the greeting has changed`
  return null
}

export function GreetingGenerator({
  agentId,
  greeting,
  voice,
  info,
  unsaved,
  readOnly,
  onChanged,
}: {
  agentId: string
  /** The agent's SAVED greeting and voice. */
  greeting: string | null
  voice: string
  info: GreetingAudioInfo | null
  /** The form has unsaved edits to the greeting, voice or model. */
  unsaved: boolean
  readOnly: boolean
  onChanged: (info: GreetingAudioInfo | null) => void
}) {
  const [call] = useState(() => new CallSession())
  const snap = useSyncExternalStore(call.subscribe, call.getSnapshot)
  const audioEl = useRef<HTMLAudioElement>(null)
  const [running, setRunning] = useState(false)
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null)
  const [playing, setPlaying] = useState(false)

  // Leaving the page ends a render in progress and silences a preview.
  useEffect(
    () => () => {
      void call.end()
      stopCurrent()
    },
    [call],
  )

  // The outcome arrives on the control channel just before the server hangs up.
  useEffect(() => {
    if (!running) return
    const outcome = snap.greetingRender
    if (outcome) {
      setRunning(false)
      void call.end()
      if (outcome.status === 'saved') {
        setMessage({ kind: 'ok', text: `Saved: ${secs(outcome.durationMs ?? 0)} in the agent’s voice.` })
        void agents
          .get(agentId, AbortSignal.timeout(15_000))
          .then((a) => onChanged(a.greetingAudio ?? null))
          .catch(() => {})
      } else {
        setMessage({ kind: 'error', text: explain(outcome.reason, outcome.heard) })
      }
      return
    }
    if (snap.phase === 'failed' || snap.phase === 'blocked' || snap.phase === 'ended') {
      setRunning(false)
      setMessage({
        kind: 'error',
        text: snap.error ?? 'The call ended before the greeting was captured. Try again.',
      })
    }
  }, [running, snap.greetingRender, snap.phase, snap.error, call, agentId, onChanged])

  const words = (greeting ?? '').trim()
  const blocked =
    readOnly
      ? null
      : unsaved
        ? 'Save the form first: the agent speaks its saved greeting in its saved voice.'
        : words === ''
          ? 'Save a greeting above first.'
          : words.includes('${')
            ? 'A greeting that fills in per-call values, like ${user}, cannot be pre-recorded. Reword it without them.'
            : null

  const generate = () => {
    if (!audioEl.current) return
    stopCurrent()
    setMessage(null)
    setRunning(true)
    void call.start({ agentId, audioEl: audioEl.current, purpose: 'greeting_render', label: 'greeting render' })
  }

  const cancel = () => {
    setRunning(false)
    void call.end()
  }

  const remove = async () => {
    stopCurrent()
    setMessage(null)
    try {
      await agents.deleteGreetingAudio(agentId, AbortSignal.timeout(15_000))
      onChanged(null)
    } catch (e) {
      setMessage({ kind: 'error', text: e instanceof ApiError ? e.message : `The clip could not be deleted: ${String(e)}` })
    }
  }

  const togglePlay = async () => {
    if (playing) {
      stopCurrent()
      setPlaying(false)
      return
    }
    if (!info) return
    setPlaying(true)
    try {
      const res = await fetch(greetingAudioPath(info.sha256))
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      await playWav(await res.arrayBuffer(), () => setPlaying(false))
    } catch (e) {
      setPlaying(false)
      setMessage({ kind: 'error', text: `The clip could not be played: ${String(e)}` })
    }
  }

  const stale = info ? staleness(info, voice, greeting) : null
  const status = running
    ? snap.phase === 'live'
      ? 'The agent is saying the greeting. Stay quiet until it finishes…'
      : 'Connecting…'
    : null

  return (
    <div className="flex max-w-2xl flex-col gap-3 rounded-md border p-3">
      {/* The render call's audio: the person hears the agent say it. */}
      <audio ref={audioEl} autoPlay className="hidden" />

      {info ? (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <Button type="button" size="sm" variant="outline" onClick={() => void togglePlay()} disabled={running}>
            {playing ? <SquareIcon className="size-3.5" /> : <PlayIcon className="size-3.5" />}
            {playing ? 'Stop' : 'Play clip'}
          </Button>
          <span className="text-muted-foreground">
            {secs(info.durationMs)} · made {fmtWhen(info.createdAt)}
            {info.createdBy ? ` by ${info.createdBy}` : ''}
          </span>
          {!readOnly && (
            <Button type="button" size="sm" variant="ghost" onClick={() => void remove()} disabled={running}>
              <Trash2Icon className="size-3.5" />
              Delete
            </Button>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">No clip yet. Until there is one, calls use the spoken greeting.</p>
      )}

      {stale && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          This clip is out of date: {stale}. Calls use the spoken greeting until you generate a new one.
        </p>
      )}

      {!readOnly && (
        <div className="flex flex-wrap items-center gap-2">
          {running ? (
            <Button type="button" size="sm" variant="outline" onClick={cancel}>
              <SquareIcon className="size-3.5" />
              Cancel
            </Button>
          ) : (
            <Button type="button" size="sm" onClick={generate} disabled={blocked !== null}>
              <SparklesIcon className="size-3.5" />
              {info ? 'Generate again' : 'Generate in the agent’s voice'}
            </Button>
          )}
          {status && <span className="text-xs text-muted-foreground">{status}</span>}
        </div>
      )}
      {blocked && !running && <p className="text-xs text-muted-foreground">{blocked}</p>}
      {!readOnly && !blocked && !running && (
        <p className="text-xs text-muted-foreground">
          Starts a short call: the agent says the greeting once, in its own voice, and that becomes the clip. It
          uses the microphone, so stay quiet until it finishes. Nothing is saved unless the agent says the
          greeting word for word.
        </p>
      )}
      {message && (
        <p className={message.kind === 'ok' ? 'text-xs text-emerald-700 dark:text-emerald-400' : 'text-xs text-destructive'}>
          {message.text}
        </p>
      )}
    </div>
  )
}
